import './account-statuses.css';

import { Trans, useLingui } from '@lingui/react/macro';
import { MenuItem } from '@szhsin/react-menu';
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'preact/hooks';
import punycode from 'punycode/';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSnapshot } from 'valtio';

import AccountInfo from '../components/account-info';
import EmojiText from '../components/emoji-text';
import Icon from '../components/icon';
import Link from '../components/link';
import Menu2 from '../components/menu2';
import Timeline from '../components/timeline';
import { api } from '../utils/api';
import isSearchEnabled from '../utils/is-search-enabled';
import mem from '../utils/mem';
import pmem from '../utils/pmem';
import showToast from '../utils/show-toast';
import states, { saveStatus } from '../utils/states';
import {
  getCurrentAccountID,
  isMediaFirstInstance,
} from '../utils/store-utils';
import supports from '../utils/supports';
import useTitle from '../utils/useTitle';

const LIMIT = 20;
const MIN_YEAR = 1983;
const { PHANPY_DEFAULT_INSTANCE: DEFAULT_INSTANCE } = import.meta.env;
const MIN_YEAR_MONTH = `${MIN_YEAR}-01`; // Birth of the Internet

const supportsInputMonth = mem(() => {
  try {
    const input = document.createElement('input');
    input.setAttribute('type', 'month');
    return input.type === 'month';
  } catch (e) {
    return false;
  }
});

function AccountStatuses({ columnMode, ...props }) {
  const { i18n, t } = useLingui();
  const snapStates = useSnapshot(states);
  const { id, ...params } = columnMode ? { id: props.id } : useParams();

  const profileSearchParamsRef = useRef(new URLSearchParams({ replies: 1 }));
  const [, forceUpdate] = useReducer((c) => c + 1, 0);
  const profileSetSearchParams = useCallback((objOrFn) => {
    const params = profileSearchParamsRef.current;
    if (typeof objOrFn === 'function') {
      objOrFn(params);
    } else if (objOrFn instanceof URLSearchParams) {
      [...params.keys()].forEach((key) => params.delete(key));
      objOrFn.forEach((value, key) => params.set(key, value));
    } else {
      Object.entries(objOrFn).forEach(([key, value]) => {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      });
    }
    forceUpdate();
  }, []);
  const [searchParams, setSearchParams] = columnMode
    ? [profileSearchParamsRef.current, profileSetSearchParams]
    : useSearchParams();
  const clearAndSetParam = useCallback(
    (paramName, paramValue) => {
      const params = new URLSearchParams(
        columnMode ? { replies: 1 } : undefined,
      );
      if (paramValue !== undefined) {
        params.set(paramName, paramValue);
      }
      setSearchParams(params);
    },
    [setSearchParams],
  );
  const toggleParam = useCallback(
    (paramName, paramValue) => {
      const params = new URLSearchParams(searchParams.toString());
      if (params.get(paramName)) {
        params.delete(paramName);
      } else {
        params.set(paramName, paramValue ?? '1');
      }
      setSearchParams(params);
    },
    [setSearchParams, searchParams],
  );

  const month = searchParams.get('month');
  const excludeReplies = !searchParams.get('replies');
  const excludeBoosts = !!searchParams.get('boosts');
  const tagged = searchParams.get('tagged');
  const media = !!searchParams.get('media');
  // When no instance is in the URL, prefer the home instance (DEFAULT_INSTANCE)
  // so all API calls — account lookup, statuses, featured tags — go to the right server.
  const { masto, instance, authenticated } = api({
    instance: params?.instance || DEFAULT_INSTANCE || undefined,
  });
  const { masto: currentMasto, instance: currentInstance } = api();
  const accountStatusesIterator = useRef();

  const allSearchParams = [month, excludeReplies, excludeBoosts, tagged, media];
  const [account, setAccount] = useState();
  const searchOffsetRef = useRef(0);
  useEffect(() => {
    searchOffsetRef.current = 0;
  }, allSearchParams);

  const mediaFirst = useMemo(() => isMediaFirstInstance(), []);

  const sameCurrentInstance = useMemo(
    () => instance === currentInstance,
    [instance, currentInstance],
  );
  const [searchEnabled, setSearchEnabled] = useState(false);
  useEffect(() => {
    // Only enable for current logged-in instance
    // Most remote instances don't allow unauthenticated searches
    if (!sameCurrentInstance) return;
    if (!account?.acct) return;
    (async () => {
      const enabled = await isSearchEnabled(instance);
      console.log({ enabled });
      setSearchEnabled(enabled);
    })();
  }, [instance, sameCurrentInstance, account?.acct]);

  async function fetchAccountStatuses(firstLoad) {
    const isValidMonth = /^\d{4}-[01]\d$/.test(month);
    const isValidYear = month?.split?.('-')?.[0] >= MIN_YEAR;
    if (isValidMonth && isValidYear) {
      if (!account) {
        return {
          value: [],
          done: true,
        };
      }
      const [_year, _month] = month.split('-');
      const monthIndex = parseInt(_month, 10) - 1;
      // YYYY-MM (no day)
      // Search options:
      // - from:account
      // - after:YYYY-MM-DD (non-inclusive)
      // - before:YYYY-MM-DD (non-inclusive)

      // Last day of previous month
      const after = new Date(_year, monthIndex, 0);
      const afterStr = `${after.getFullYear()}-${(after.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${after.getDate().toString().padStart(2, '0')}`;
      // First day of next month
      const before = new Date(_year, monthIndex + 1, 1);
      const beforeStr = `${before.getFullYear()}-${(before.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${before.getDate().toString().padStart(2, '0')}`;
      console.log({
        month,
        _year,
        _month,
        monthIndex,
        after,
        before,
        afterStr,
        beforeStr,
      });

      let limit;
      if (firstLoad) {
        limit = LIMIT + 1;
        searchOffsetRef.current = 0;
      } else {
        limit = LIMIT + searchOffsetRef.current + 1;
        searchOffsetRef.current += LIMIT;
      }

      const searchResults = await masto.v2.search.list({
        q: `from:${account.acct} after:${afterStr} before:${beforeStr}`,
        type: 'statuses',
        limit,
        offset: searchOffsetRef.current,
      });
      if (searchResults?.statuses?.length) {
        const value = searchResults.statuses.slice(0, LIMIT);
        value.forEach((item) => {
          saveStatus(item, instance);
        });
        const done = searchResults.statuses.length <= LIMIT;
        return { value, done };
      } else {
        return { value: [], done: true };
      }
    }

    // Use resolved numeric ID from account if available (id may be a username)
    const effectiveId = account?.id || id;
    if (!account?.id && !isAccountId(id)) {
      // Username not yet resolved; timeline will retry when account loads
      return { value: [], done: true };
    }

    let results = [];
    if (firstLoad && !columnMode) {
      const { value } = await masto.v1.accounts
        .$select(effectiveId)
        .statuses.list({
          pinned: true,
        })
        .values()
        .next();
      if (value?.length && !tagged && !media) {
        const pinnedStatuses = value.map((status) => {
          saveStatus(status, instance);
          return {
            ...status,
            _pinned: true,
          };
        });
        if (pinnedStatuses.length >= 3) {
          const pinnedStatusesIds = pinnedStatuses.map((status) => status.id);
          results.push({
            id: pinnedStatusesIds,
            items: pinnedStatuses,
            type: 'pinned',
          });
        } else {
          results.push(...pinnedStatuses);
        }
      }
    }
    if (firstLoad || !accountStatusesIterator.current) {
      accountStatusesIterator.current = masto.v1.accounts
        .$select(effectiveId)
        .statuses.list({
          limit: LIMIT,
          exclude_replies: excludeReplies,
          exclude_reblogs: excludeBoosts,
          only_media: media || undefined,
          tagged,
        })
        .values();
    }
    const { value, done } = await accountStatusesIterator.current.next();
    if (value?.length) {
      if (!supports('@mastodon/pinned-posts')) {
        // Check if value is same as pinned post (results)
        // If the index for every post is the same, means API might not support pinned posts
        // TODO: This is a really weird check, fix this at some point
        if (results.length) {
          let pinnedStatusesIds = [];
          if (results[0]?.type === 'pinned') {
            pinnedStatusesIds = results[0].id;
          } else {
            pinnedStatusesIds = results
              .filter((status) => status._pinned)
              .map((status) => status.id);
          }
          const containsAllPinned = pinnedStatusesIds.every((postId) =>
            value.some((status) => status.id === postId),
          );
          if (containsAllPinned) {
            // Remove pinned posts
            results = [];
          }
        }
      }

      results.push(...value);

      value.forEach((item) => {
        saveStatus(item, instance);
      });
    }
    return {
      value: results,
      done,
    };
  }

  const [featuredTags, setFeaturedTags] = useState([]);

  let title = t`Account posts`;
  if (account?.acct) {
    const acctDisplay = (/@/.test(account.acct) ? '' : '@') + account.acct;
    const accountDisplay = account?.displayName
      ? `${account.displayName} (${acctDisplay})`
      : acctDisplay;
    if (tagged && media) {
      title = t`Media posts tagged #${tagged} by ${accountDisplay}`;
    } else if (tagged) {
      title = t`Posts tagged #${tagged} by ${accountDisplay}`;
    } else if (month) {
      const [y, m] = month.split('-');
      const monthYear = new Date(+y, +m - 1, 1).toLocaleString(i18n.locale, {
        month: 'long',
        year: 'numeric',
      });
      title = t`Posts in ${monthYear} by ${accountDisplay}`;
    } else if (media) {
      title = t`Media posts by ${accountDisplay}`;
    } else {
      title = accountDisplay;
    }
  }
  useTitle(title, '/:instance?/a/:id');

  const fetchAccount = useCallback(() => {
    return memFetchAccount(id, masto, instance);
  }, [id, masto, instance]);

  useEffect(() => {
    (async () => {
      let resolvedId = id;
      try {
        const acc = await fetchAccount();
        console.log(acc);
        setAccount(acc);
        resolvedId = acc.id;
      } catch (e) {
        console.error(e);
      }
      // No need, because the whole filter bar is hidden
      // TODO: Revisit this
      if (!mediaFirst) {
        try {
          const featuredTags = await masto.v1.accounts
            .$select(resolvedId)
            .featuredTags.list();
          console.log({ featuredTags });
          setFeaturedTags(featuredTags);
        } catch (e) {
          console.error(e);
        }
      }
    })();
  }, [id, mediaFirst]);

  const { displayName, acct, emojis } = account || {};

  const isSelf = useMemo(
    () => account?.id === getCurrentAccountID(),
    [account?.id],
  );

  const filterBarRef = useRef();
  const TimelineStart = useMemo(() => {
    const repliesFiltered = columnMode ? excludeReplies : !excludeReplies;
    const filtered =
      repliesFiltered || excludeBoosts || tagged || media || !!month;
    const cachedAccount = snapStates.accounts[`${id}@${instance}`];

    const buildParamStr = (updates) => {
      const params = new URLSearchParams(searchParams.toString());
      for (const [key, val] of Object.entries(updates)) {
        if (val == null) {
          params.delete(key);
        } else {
          params.set(key, val);
        }
      }
      const str = params.toString();
      return str ? `?${str}` : '';
    };

    return (
      <>
        <AccountInfo
          instance={instance}
          account={cachedAccount || id}
          fetchAccount={fetchAccount}
          authenticated={authenticated}
          standalone
          showEndorsements
        />
        {!mediaFirst && (
          <div
            class="filter-bar"
            ref={filterBarRef}
            style={{
              position: 'relative',
            }}
          >
            {filtered ? (
              <Link
                to={`/${instance}/a/${id}`}
                class="insignificant filter-clear"
                title={t`Reset filters`}
                key="clear-filters"
                onClick={(e) => {
                  if (columnMode) {
                    e.preventDefault();
                    clearAndSetParam();
                  }
                }}
              >
                <Icon icon="x" size="l" alt={t`Reset filters`} />
              </Link>
            ) : (
              <Icon
                icon="filter"
                class="insignificant"
                size="l"
                alt={t`Filters`}
              />
            )}
            <div class="filter-bar-group">
              <label>
                <input
                  type="checkbox"
                  checked={!excludeReplies}
                  disabled={!!month}
                  onChange={() => {
                    toggleParam('replies', '1');
                    if (excludeReplies) {
                      showToast(t`Showing replies`);
                    } else {
                      showToast(t`Hiding replies`);
                    }
                  }}
                />
                <Trans>Replies</Trans>
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={!excludeBoosts}
                  disabled={!!month}
                  onChange={() => {
                    toggleParam('boosts', '0');
                    if (excludeBoosts) {
                      showToast(t`Showing boosts`);
                    } else {
                      showToast(t`Hiding boosts`);
                    }
                  }}
                />
                <Trans>Boosts</Trans>
              </label>
            </div>
            <Link
              to={`/${instance}/a/${id}${buildParamStr({
                media: media ? null : '1',
              })}`}
              onClick={(e) => {
                if (columnMode) {
                  e.preventDefault();
                  toggleParam('media', '1');
                }
                if (!media) {
                  showToast(t`Showing posts with media`);
                }
              }}
              class={media ? 'is-active' : ''}
            >
              <Trans>Media</Trans>
            </Link>
            {featuredTags.length > 0 && (
              <div class="filter-bar-group">
                {[...featuredTags]
                  .sort((a, b) => {
                    if (a.name === tagged) return -1;
                    if (b.name === tagged) return 1;
                    return 0;
                  })
                  .map((tag) => (
                    <Link
                      key={tag.id}
                      to={`/${instance}/a/${id}${buildParamStr({
                        tagged: tagged === tag.name ? null : tag.name,
                      })}`}
                      onClick={(e) => {
                        if (columnMode) {
                          e.preventDefault();
                          const params = new URLSearchParams(
                            searchParams.toString(),
                          );
                          if (params.get('tagged') === tag.name) {
                            params.delete('tagged');
                          } else {
                            params.set('tagged', tag.name);
                          }
                          setSearchParams(params);
                        }
                        if (tagged !== tag.name) {
                          showToast(t`Showing posts tagged with #${tag.name}`);
                        }
                      }}
                      class={tagged === tag.name ? 'is-active' : ''}
                    >
                      <span>
                        <span class="more-insignificant">#</span>
                        {tag.name}
                      </span>
                      {/* <span class="filter-count">{tag.statusesCount}</span> */}
                    </Link>
                  ))}
              </div>
            )}
            {searchEnabled && !columnMode && (
              <>
                <div class="filter-bar-separator" />
                {supportsInputMonth() ? (
                  <label class={`filter-field ${month ? 'is-active' : ''}`}>
                    <Icon icon="month" size="l" />
                    <input
                      type="month"
                      disabled={!account?.acct}
                      value={month || ''}
                      min={MIN_YEAR_MONTH}
                      max={new Date().toISOString().slice(0, 7)}
                      onInput={(e) => {
                        const { value, validity } = e.currentTarget;
                        if (!validity.valid) return;
                        setSearchParams(
                          value
                            ? {
                                month: value,
                              }
                            : {},
                        );
                        const [year, month] = value.split('-');
                        const monthIndex = parseInt(month, 10) - 1;
                        const date = new Date(year, monthIndex);
                        showToast(
                          t`Showing posts in ${date.toLocaleString(
                            i18n.locale,
                            {
                              month: 'long',
                              year: 'numeric',
                            },
                          )}`,
                        );
                      }}
                    />
                  </label>
                ) : (
                  // Fallback to <select> for month and <input type="number"> for year
                  <MonthPicker
                    class={`filter-field ${month ? 'is-active' : ''}`}
                    disabled={!account?.acct}
                    value={month || ''}
                    min={MIN_YEAR_MONTH}
                    max={new Date().toISOString().slice(0, 7)}
                    onInput={(e) => {
                      const { value, validity } = e;
                      if (!validity.valid) return;
                      setSearchParams(
                        value
                          ? {
                              month: value,
                            }
                          : {},
                      );
                    }}
                  />
                )}
                <button
                  type="button"
                  class="filter-field"
                  onClick={() => {
                    states.showSearchCommand = {
                      query: isSelf ? 'from:me ' : `from:${account?.acct} `,
                    };
                  }}
                >
                  <Icon
                    icon="search"
                    size="l"
                    alt={
                      isSelf
                        ? t`Search my posts`
                        : t`Search @${account?.username}'s posts`
                    }
                  />
                </button>
              </>
            )}
          </div>
        )}
      </>
    );
  }, [
    id,
    instance,
    authenticated,
    featuredTags,
    fetchAccount,
    searchEnabled,
    ...allSearchParams,
  ]);

  useEffect(() => {
    const activeEls = [
      ...(filterBarRef.current?.querySelectorAll('.is-active') ?? []),
    ];
    if (!activeEls.length) return;
    const barWidth = filterBarRef.current.offsetWidth;
    const left = Math.min(...activeEls.map((el) => el.offsetLeft));
    const right = Math.max(
      ...activeEls.map((el) => el.offsetLeft + el.offsetWidth),
    );
    const spanWidth = right - left;
    filterBarRef.current.scrollTo({
      behavior: 'smooth',
      left: spanWidth >= barWidth ? left : left - (barWidth - spanWidth) / 2,
    });
  }, [featuredTags, searchEnabled, ...allSearchParams]);

  const accountInstance = useMemo(() => {
    if (!account?.url) return null;
    const domain = URL.parse(account.url)?.hostname;
    return domain;
  }, [account]);
  const sameInstance = instance === accountInstance;
  const allowSwitch = !!account && !sameInstance;

  return (
    <>
      <Timeline
        key={id}
        title={`${account?.acct ? '@' + account.acct : t`Posts`}`}
        titleComponent={
          <h1
            class="header-double-lines header-account"
            // onClick={() => {
            //   states.showAccount = {
            //     account,
            //     instance,
            //   };
            // }}
          >
            <b>
              <EmojiText
                text={displayName}
                emojis={emojis}
                resolverURL={account?.url}
              />
            </b>
            <div>
              <span class="bidi-isolate">@{acct}</span>
            </div>
          </h1>
        }
        id="account-statuses"
        instance={instance}
        emptyText={t`Nothing to see here yet.`}
        errorText={t`Unable to load posts`}
        fetchItems={fetchAccountStatuses}
        useItemID
        view={media || mediaFirst ? 'media' : undefined}
        boostsCarousel={!isSelf && snapStates.settings.boostsCarousel}
        timelineStart={TimelineStart}
        refresh={[
          excludeReplies,
          excludeBoosts,
          tagged,
          media,
          month + account?.acct,
        ].toString()}
        headerEnd={
          <Menu2
            portal
            // setDownOverflow
            overflow="auto"
            viewScroll="close"
            position="anchor"
            menuButton={
              <button type="button" class="plain">
                <Icon icon="more" size="l" alt={t`More`} />
              </button>
            }
          >
            <MenuItem
              disabled={!allowSwitch}
              onClick={() => {
                (async () => {
                  try {
                    const { masto } = api({
                      instance: accountInstance,
                    });
                    const acc = await masto.v1.accounts.lookup({
                      acct: account.acct,
                    });
                    const { id } = acc;
                    location.hash = `/${accountInstance}/a/${id}`;
                  } catch (e) {
                    console.error(e);
                    alert(t`Unable to fetch account info`);
                  }
                })();
              }}
            >
              <Icon icon="transfer" />{' '}
              <small class="menu-double-lines">
                <Trans>
                  Switch to account's server{' '}
                  {accountInstance ? (
                    <>
                      {' '}
                      (<b>{punycode.toUnicode(accountInstance)}</b>)
                    </>
                  ) : null}
                </Trans>
              </small>
            </MenuItem>
            {!sameCurrentInstance && (
              <MenuItem
                onClick={() => {
                  (async () => {
                    try {
                      const acc = await currentMasto.v1.accounts.lookup({
                        acct: account.acct + '@' + instance,
                      });
                      const { id } = acc;
                      location.hash = `/${currentInstance}/a/${id}`;
                    } catch (e) {
                      console.error(e);
                      alert(t`Unable to fetch account info`);
                    }
                  })();
                }}
              >
                <Icon icon="transfer" />{' '}
                <small class="menu-double-lines">
                  <Trans>
                    Switch to my server (<b>{currentInstance}</b>)
                  </Trans>
                </small>
              </MenuItem>
            )}
          </Menu2>
        }
      />
      {acct && !isSelf && (
        <data
          class="compose-data"
          value={JSON.stringify({
            draftStatus: {
              status: `@${acct} `,
            },
          })}
        />
      )}
    </>
  );
}

function MonthPicker(props) {
  const { i18n } = useLingui();
  const {
    class: className,
    disabled,
    value,
    min,
    max,
    onInput = () => {},
  } = props;
  const [_year, _month] = value?.split('-') || [];
  const monthFieldRef = useRef();
  const yearFieldRef = useRef();

  const checkValidity = (month, year) => {
    const [minYear, minMonth] = min?.split('-') || [];
    const [maxYear, maxMonth] = max?.split('-') || [];
    if (year < minYear) return false;
    if (year > maxYear) return false;
    if (year === minYear && month < minMonth) return false;
    if (year === maxYear && month > maxMonth) return false;
    return true;
  };

  return (
    <div class={className}>
      <Icon icon="month" size="l" />
      <select
        ref={monthFieldRef}
        disabled={disabled}
        value={_month || ''}
        onInput={(e) => {
          const { value: month } = e.currentTarget;
          const year = yearFieldRef.current.value;
          if (!checkValidity(month, year))
            return {
              value: '',
              validity: {
                valid: false,
              },
            };
          onInput({
            value: month ? `${year}-${month}` : '',
            validity: {
              valid: true,
            },
          });
        }}
      >
        <option value="">
          <Trans>Month</Trans>
        </option>
        <option disabled>-----</option>
        {Array.from({ length: 12 }, (_, i) => (
          <option
            value={
              // Month is 1-indexed
              (i + 1).toString().padStart(2, '0')
            }
            key={i}
          >
            {new Date(0, i).toLocaleString(i18n.locale, {
              month: 'long',
            })}
          </option>
        ))}
      </select>{' '}
      <input
        ref={yearFieldRef}
        type="number"
        disabled={disabled}
        value={_year || new Date().getFullYear()}
        min={min?.slice(0, 4) || MIN_YEAR}
        max={max?.slice(0, 4) || new Date().getFullYear()}
        onInput={(e) => {
          const { value: year, validity } = e.currentTarget;
          const month = monthFieldRef.current.value;
          if (!validity.valid || !checkValidity(month, year))
            return {
              value: '',
              validity: {
                valid: false,
              },
            };
          onInput({
            value: year ? `${year}-${month}` : '',
            validity: {
              valid: true,
            },
          });
        }}
        style={{
          width: '4.5em',
        }}
      />
    </div>
  );
}

// Returns true if str looks like a numeric or ULID account ID (not a username)
function isAccountId(str) {
  return /^\d+$/.test(str) || str.length >= 16;
}

async function fetchAccount(id, masto, instance) {
  if (!isAccountId(id)) {
    // masto is already pointed at the correct instance (DEFAULT_INSTANCE when
    // no instance prefix is in the URL), so a plain lookup by username is enough.
    return masto.v1.accounts.lookup({ acct: id });
  }
  return masto.v1.accounts.$select(id).fetch();
}
const memFetchAccount = pmem(fetchAccount, {
  expires: 30 * 60 * 1000, // 30 minutes
});

export default AccountStatuses;
